import { channel, type Channel } from "redux-saga";
import { all, call, put, take, takeEvery, takeLeading, type SagaReturnType } from "redux-saga/effects";

import { toastShowErrorAction, toastShowSuccessAction } from "@ds/modules/notifications/redux/actions";

import { storageService, type ProgressInfo, type StorageMethodConfig } from "@ds/services/storage";
import { UnexpectedError } from "@ds/utils/errors";
import { logger } from "@ds/utils/logger";
import { isErrorLike } from "@ds/utils/type-guards/error-guards";

import { assetsActions, type AssetsProgressChannelEvent } from "../slice";
import { getMainTableTypeByApiContentType } from "../../utils/helpers";
import { createThumbnailModel } from "../../utils/thumbnail-model";
import { thumbnailsService } from "../../utils/thumbnails-api";

function* deleteAssetThumbnail({ payload, meta }: ReturnType<typeof assetsActions.deleteAssetThumbnail>) {
  try {
    yield call([thumbnailsService, thumbnailsService.deleteThumbnail], payload);
    yield put(assetsActions.deleteAssetThumbnailSucceeded(payload, meta));
    yield put(toastShowSuccessAction("Asset thumbnail was deleted successfully"));
  } catch (err) {
    const errorTitle = "Delete asset thumbnail";
    if (isErrorLike(err)) {
      yield put(assetsActions.deleteAssetThumbnailFailed(payload, meta, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* uploadAssetThumbnail(
  progressChannel: Channel<AssetsProgressChannelEvent>,
  { payload, meta }: ReturnType<typeof assetsActions.uploadAssetThumbnail>,
) {
  try {
    const uniqueKey = `assets/${new Date().getTime()}_${payload.thumbnailFile.name}`;
    const storageConfig: StorageMethodConfig = {
      level: "public",
      contentType: payload.thumbnailFile.type,
      progressCallback: (progressInfo: ProgressInfo) => progressChannel.put({ progressInfo }),
    };

    const { key }: { key: string } = yield call(
      [storageService, storageService.put],
      uniqueKey,
      payload.thumbnailFile,
      storageConfig,
    );

    const result: SagaReturnType<typeof thumbnailsService.createThumbnail> = yield call(
      [thumbnailsService, thumbnailsService.createThumbnail],
      createThumbnailModel({
        key,
        asset_id: payload.asset.id,
        file_name: payload.thumbnailFile.name,
        file_size: payload.thumbnailFile.size,
        content_type: payload.thumbnailFile.type,
      }),
    );

    yield put(assetsActions.uploadAssetThumbnailSucceeded(result, meta));
    yield put(toastShowSuccessAction("Asset thumbnail was successfully uploaded"));
  } catch (err) {
    const errorTitle = "Upload asset thumbnail";
    if (isErrorLike(err)) {
      yield put(assetsActions.uploadAssetThumbnailFailed(payload, meta, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* uploadAssetThumbnails(
  progressChannel: Channel<AssetsProgressChannelEvent>,
  { payload, meta }: ReturnType<typeof assetsActions.uploadAssetThumbnails>,
) {
  yield all(
    payload.map(entity =>
      call(
        uploadAssetThumbnail,
        progressChannel,
        assetsActions.uploadAssetThumbnail(entity, getMainTableTypeByApiContentType(meta.filters?.contentType)),
      ),
    ),
  );
}

export function* watchAssetThumbnails() {
  yield takeLeading(assetsActions.deleteAssetThumbnail, deleteAssetThumbnail);

  const progressChannel: Channel<AssetsProgressChannelEvent> = yield call(channel);
  yield takeEvery(assetsActions.uploadAssetThumbnails, uploadAssetThumbnails, progressChannel);

  while (true) {
    const { progressInfo }: AssetsProgressChannelEvent = yield take(progressChannel);
    yield put(assetsActions.progressUploadingAssetThumbnail(progressInfo));
  }
}
